import type { DoneActorEvent, ErrorActorEvent, StateFrom } from 'xstate';
import { assign, forwardTo, setup } from 'xstate';
import { positionAvailabilityWorkflow } from '~/features/cart/state-machine/positionAvailabilityMachine';
import { guardHasAvailabilityOptions } from '~/features/cart/state-machine/guards';
import type { CartContextType, CartMessage} from '~/features/cart/state-machine/context';
import { createCartContext } from '~/features/cart/state-machine/context';
import type { CartResponseData, PositionIntentActorResponse } from '~/features/cart/state-machine/types';
import { CANCEL_REASONS } from '~/features/cart/state-machine/types';
import { CartEventTypes } from '~/features/cart/state-machine/events';
import {
  cancelOrderActor,
  createOrderActor,
  fetchCartStatusActor,
  fetchOrderActor,
  publishPositionsRemovedIfSuccessful,
  processPositionIntentActor,
  refreshOrderActor,
  sendOrderActor,
  setDeliveryDateActor
} from '~/features/cart/state-machine/backend-access/actors';
import { captureError } from '~/tracking/logger';
import { logToConsole } from '~/logging/consoleLogger';
import { clearCartTraces, getCartTraces } from '~/features/cart/cartMachineLogger';
import { publishShoppingCartBroadcast } from '~/features/cart/cartBroadcast';
import { HAS_BLOCKED_POSITIONS, PROLIX_ERROR_IDENTIFIER } from '../../../common/types/prolixApplicationError';
import  type { ICartResponseDtoData, ICartStatusResponseDtoData } from '../../../dtos/ProdegaApiWebshop-api-typings-generated';
import { getCurrentRouteId } from '~/features/cart/state-machine/currentRouteTracker';
import { ROUTE_IDS } from '~/route-constants';
import { isLoginExpiredError } from '~/features/cart/state-machine/cartError';

const POSITION_AVAILABILITY_OPTIONS_ACTOR = 'positionAvailabilityOptionsActor';
export const POSITION_AVAILABILITY_OPTIONS_ACTOR_ID = 'positionAvailabilityOptionsActorId';

export type CartMachineState = StateFrom<typeof cartMachine>;

function apiResponseSignalsBlockedPositionsButShouldBeSuppressed(apiResponse: ICartResponseDtoData | PositionIntentActorResponse | undefined, context: CartContextType): boolean {
  if (!apiResponse) return false;
  if (apiResponse[PROLIX_ERROR_IDENTIFIER] !== HAS_BLOCKED_POSITIONS) return false;

  // WEB-3468, WEB-3819:
  // Wenn das Backend den Fehler "Positionen mit blockiertem Status" liefert, kann der Benutzer dies "akzeptieren". 
  // Dies ist dann im Context der State-Machine festgehalten.
  // Bei weiteren API-Calls liefert das Backend immer wieder diesen Fehler, aber der Dialog soll nicht jedes
  // Mal wieder angezeigt werden resp. die State-Machine soll dann nicht mehr in den CartError Zustand gehen.
  const dialogWasDismissedBefore = context.blockedPositionsDialogAcknowledgedForCartId === apiResponse.order?.id;

  // Der Dialog "Blockierte Positionen" soll nur auf der Cart-Route erscheinen.
  // Off-Route unterdrücken wir die Transition; beim Betreten der Cart-Route lädt CartIndexRoute den Warenkorb
  // neu, der Guard wird erneut evaluiert und die Transition in #CartError greift wie üblich.
  const notOnCartRoute = getCurrentRouteId() !== ROUTE_IDS.CART;

  return dialogWasDismissedBefore || notOnCartRoute;
}

const cartExpiredTransition = {
  guard: ({ event }: { event: DoneActorEvent<CartResponseData | ICartStatusResponseDtoData> }) =>
    event.output.expirationReason === 'CART_EXPIRED',
  target: '#CartExpired'
};
const deliveryDateExpiredTransition = {
  guard: ({ event }: { event: DoneActorEvent<CartResponseData | ICartStatusResponseDtoData> }) =>
    event.output.expirationReason === 'INVALID_DELIVERY_DATE',
  target: '#DeliveryDateExpired'
};
const apiCallErrorTransition = {
  guard: ({ event, context }) => {
    return (
      !!event.output.error ||
      (event.output.prolixError &&
        !apiResponseSignalsBlockedPositionsButShouldBeSuppressed(event.output, context) &&
        // Wenn positionAvailabilityOptions zurückgeliefert werden, dann soll immer der Verfügbarkeits-Optionen-Dialog 
        // angezeigt werden, auch wenn die betroffenen Positionen dann bereits im `BLOCKED_NOT_AVAILABLE` Status sind.
        (event.output.positionAvailabilityOptions?.length ?? 0) <= 0)
    );
  },
  target: '#CartError',
};


const defaultTransitions = [cartExpiredTransition, deliveryDateExpiredTransition, apiCallErrorTransition] as const;

const loginExpiredTransition = {
  guard: ({ event }: { event: ErrorActorEvent<unknown, string> }) => {
    return isLoginExpiredError(event.error);
  },
  target: '#LoginExpired'
};
const genericErrorTransition = {
  target: '#CartError'
};
const errorTransitions = [loginExpiredTransition, genericErrorTransition] as const;

export const cartMachine = setup({
  types: {
    context: {} as CartContextType,
    events: CartEventTypes
  },
  actors: {
    fetchOrder: fetchOrderActor,
    fetchCartStatus: fetchCartStatusActor,
    refreshOrder: refreshOrderActor,
    createNewOrder: createOrderActor,
    sendOrder: sendOrderActor,
    cancelOrder: cancelOrderActor,
    setDeliveryDate: setDeliveryDateActor,
    processPositionIntent: processPositionIntentActor,
    [POSITION_AVAILABILITY_OPTIONS_ACTOR]: positionAvailabilityWorkflow
  }
}).createMachine({
  id: 'cart',
  initial: 'NotLoggedIn',
  context: createCartContext(),
  on: {
    logout: '.NotLoggedIn',
    abortUserInputForCart: {
      actions: assign({
        cartMessages: () => []
      })
    },
    resetBlockedPositionsAcknowledge: {
      actions: assign({
        blockedPositionsDialogAcknowledgedForCartId: () => null
      })
    },
    userOptionSelected: { actions: forwardTo(POSITION_AVAILABILITY_OPTIONS_ACTOR_ID) }
  },
  states: {
    CartError: {
      id: 'CartError',
      entry: [
        assign({
          cartMessages: ({ event, context }) => {
            const messagesFromApi = [] as CartMessage[];
            if ((event as any)?.error) {
              // technische Fehler: das `error` Objektes wird in actors.ts erstellt
              const messageText = (event as any).error.message;
              const prolixErrorIdentifier = (event as any).error[PROLIX_ERROR_IDENTIFIER];
              const traceId = (event as any).error.traceId as string | undefined;
              messagesFromApi.push({ messageText, [PROLIX_ERROR_IDENTIFIER]: prolixErrorIdentifier, traceId });
            }
            if (
              ((event as any)?.output?.messages && (event as any).output.messages.length > 0) ||
              (event as any).output?.[PROLIX_ERROR_IDENTIFIER]
            ) {
              // fachliche Fehler: `output` ist eine CartResponse
              const prolixError = (event as any).output.prolixError;
              const prolixErrorIdentifier = (event as any).output[PROLIX_ERROR_IDENTIFIER];
              if ((event as any)?.output?.messages && (event as any).output.messages.length > 0) {
                const messages = (event as any).output.messages as string[];
                messagesFromApi.push(
                  ...messages.map(m => ({
                    messageText: m,
                    prolixError,
                    [PROLIX_ERROR_IDENTIFIER]: prolixErrorIdentifier
                  }))
                );
              } else {
                messagesFromApi.push({
                  messageText: undefined,
                  prolixError,
                  [PROLIX_ERROR_IDENTIFIER]: prolixErrorIdentifier
                });
              }
            }

            const updatedCartMessages = [...(context.cartMessages ?? []), ...messagesFromApi];
            return updatedCartMessages;
          },
          currentOrder: ({ event, context }) => (event as any)?.output?.order ?? context.currentOrder
        }),
        ({ self, event, context }) => {
          const isBlockedPositionsError = context.cartMessages.some(
            m => m[PROLIX_ERROR_IDENTIFIER] === HAS_BLOCKED_POSITIONS
          );
          if (isBlockedPositionsError) return;
          const cartTraces = getCartTraces();
          const info = {
            state: self.getSnapshot().value,
            error: (event as any)?.error ?? (event as any)?.output?.error,
            event: event,
            context: context,
            cartTraces
          };
          captureError(`Error in shopping cart: ${context.cartMessages.map(m => m.messageText).join(', ')}`, info);
        }
      ],
      on: {
        reloadShoppingCart: 'Init',
        cancelCart: {
          target: 'CancellingOrder',
          actions: assign({
            // Wichtig: currentOrder muss auf undefined gesetzt werden, damit das Frontend die orderId nicht an das Backend schickt.
            // Falls die orderId an das Backend geschickt würde, würde das Backend eine Prüfung machen, ob diese
            // orderId noch der aktuelle Warenkorb ist, was hier im Szenario "Recovery aus dem Error-Fall" nicht gewünscht ist.
            currentOrder: () => undefined,
            cancelReasonId: () => CANCEL_REASONS.CANCELLED_BY_CUSTOMER
          })
        },
        changeDeliveryDateOfCart: 'ChangingDeliveryDateOfCart',
        acknowledgeBlockedPositionsDialog: {
          target: 'Idle',
          actions: assign({
            cartMessages: () => [],
            blockedPositionsDialogAcknowledgedForCartId: ({ context }) => context.currentOrder?.id ?? null
          })
        }
      }
    },
    LoginExpired: {
      id: 'LoginExpired',
      entry: assign(createCartContext())
    },
    NotLoggedIn: {
      entry: [assign(createCartContext())],
      on: {
        login: 'Init'
      }
    },
    Init: {
      entry: assign(createCartContext()),
      invoke: {
        src: 'fetchOrder',
        onDone: [
          ...defaultTransitions,
          {
            target: 'Idle',
            actions: assign({
              currentOrder: ({ event }) => {
                return event.output.order;
              }
            })
          }
        ],
        onError: [...errorTransitions]
      }
    },
    CartExpired: {
      id: 'CartExpired',
      on: {
        refreshShoppingCart: 'CartRefreshing',
        cancelCart: {
          target: 'CancellingOrder',
          actions: assign({ cancelReasonId: () => CANCEL_REASONS.ORDER_EXPIRED })
        }
      }
    },
    DeliveryDateExpired: {
      id: 'DeliveryDateExpired',
      initial: 'UserInputDeliveryDate',
      on: {
        cancelCart: {
          target: 'CancellingOrder',
          actions: assign({ cancelReasonId: () => CANCEL_REASONS.ORDER_EXPIRED })
        }
      },
      states: {
        UserInputDeliveryDate: {
          on: {
            chooseDeliveryDate: {
              target: 'ProcessingChangeDeliveryDateOfCart',
              actions: assign({
                desiredDeliveryDate: ({ event }) => event.desiredDeliveryDate
              })
            }
          }
        },
        ProcessingChangeDeliveryDateOfCart: {
          invoke: {
            src: 'setDeliveryDate',
            input: ({ context }) => ({
              desiredDeliveryDate: context.desiredDeliveryDate! as any, // das Frontend sendet einen String, welchen das Backend in ein Datum parst
              orderId: context.currentOrder?.id
            }),
            onDone: [
              ...defaultTransitions,
              {
                guard: guardHasAvailabilityOptions,
                target: 'HandlingPositionAvailabilityOptions',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order,
                  cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                  positionAvailabilityOptions: ({ event }) => event.output.positionAvailabilityOptions
                })
              },
              {
                target: 'FinishedChangeDeliveryDateOfCart',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order,
                  cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                  positionAvailabilityOptions: () => undefined
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        HandlingPositionAvailabilityOptions: {
          invoke: {
            id: POSITION_AVAILABILITY_OPTIONS_ACTOR_ID,
            src: POSITION_AVAILABILITY_OPTIONS_ACTOR,
            input: ({ context }) => context.positionAvailabilityOptions!,
            onDone: [
              ...defaultTransitions,
              {
                target: 'FinishedChangeDeliveryDateOfCart',
                actions: assign({
                  positionAvailabilityOptions: () => undefined,
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        FinishedChangeDeliveryDateOfCart: {
          type: 'final'
        }
      },
      onDone: {
        target: 'Idle'
      }
    },
    CartRefreshing: {
      invoke: {
        src: 'refreshOrder',
        input: ({ context }) => context.currentOrder?.id,
        onDone: [
          ...defaultTransitions,
          {
            target: 'ProcessingPositions',
            actions: assign(({ context, event }) => {
              // Nach dem Refresh des Warenkorbs sollen alle noch ausstehenden Positions-Änderungen (Intents),
              // die zwischenzeitlich nicht verarbeitet werden konnten, erneut auf den aktualisierten Warenkorb angewendet werden.
              const pendingPositionIntents = context.scheduledPositionIntent
                ? [context.scheduledPositionIntent, ...context.positionIntents]
                : context.positionIntents;
              return {
                ...createCartContext(),
                positionIntents: pendingPositionIntents,
                currentOrder: event.output.order
              };
            })
          }
        ],
        onError: [...errorTransitions]
      }
    },
    CancellingOrder: {
      entry: [
        assign({
          positionIntents: () => [],
          scheduledPositionIntent: () => undefined,
          cartMessages: () => []
        })
      ],
      invoke: {
        src: 'cancelOrder',
        input: ({ context }) => ({
          cancelReasonId: context.cancelReasonId ?? CANCEL_REASONS.CANCELLED_BY_CUSTOMER,
          orderId: context.currentOrder?.id
        }),
        onDone: [
          ...defaultTransitions,
          {
            target: 'Idle',
            actions: [
              ({ event, context }) => {
                publishPositionsRemovedIfSuccessful(event.output, context.currentOrder);
              },
              assign({
                currentOrder: () => undefined,
                cancelReasonId: () => undefined,
                blockedPositionsDialogAcknowledgedForCartId: () => null
              })
            ]
          }
        ],
        onError: [...errorTransitions]
      }
    },
    SendingOrder: {
      invoke: {
        src: 'sendOrder',
        input: ({ context }) => ({
          cartParams: context.sendCartParams,
          orderId: context.currentOrder!.id
        }),
        onDone: [
          ...defaultTransitions,
          {
            guard: ({ event }) => !!event.output.ociCustomerOrderMessage,
            target: 'OciSubmitForm',
            actions: assign({
              completedOrderId: ({ context }) => context.currentOrder?.id,
              currentOrder: () => undefined,
              ociCustomerOrderMessage: ({ event }) => event.output.ociCustomerOrderMessage,
              cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
              blockedPositionsDialogAcknowledgedForCartId: () => null
            })
          },
          {
            target: 'Idle',
            actions: [
              ({ event, context }) => {
                if (event.output.order?.successfulSent) {
                  publishShoppingCartBroadcast('CART_SUCCESSFULLY_SENT', { order: context.currentOrder! });
                  clearCartTraces();
                }
              },
              assign({
                currentOrder: ({ event, context }) =>
                  event.output.order?.successfulSent ? undefined : context.currentOrder,
                completedOrderId: ({ event }) =>
                  event.output.order?.successfulSent ? event.output.order?.id : undefined,
                cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                blockedPositionsDialogAcknowledgedForCartId: () => null
              })
            ]
          }
        ],
        onError: [...errorTransitions]
      }
    },
    OciSubmitForm: {},
    Idle: {
      id: 'Idle',
      entry: [
        assign({
          isInitialized: () => true,
          isCsvImport: () => false,
          hasBackgroundCartReloadError: () => false
        })
      ],
      initial: 'IdleWaiting',
      states: {
        IdleWaiting: {
          on: {
            reloadShoppingCart: {
              guard: ({ context }) => !context.hasBackgroundCartReloadError,
              target: 'IdleReloading'
            }
          }
        },
        IdleReloading: {
          invoke: {
            src: 'fetchOrder',
            input: ({ context }) => context.currentOrder?.id,
            onDone: [
              ...defaultTransitions,
              {
                target: 'IdleWaiting',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: {
              target: 'IdleWaiting',
              actions: [
                assign({
                  hasBackgroundCartReloadError: () => true
                }),
                args =>
                  logToConsole({
                    loggerName: 'cart-machine',
                    logLevel: 'INFO',
                    message: 'Error while reloading the cart in the background',
                    args: [args?.event?.error]
                  })
              ]
            }
          }
        }
      },
      on: {
        addPositionIntent: {
          target: 'ProcessingPositions',
          actions: assign({
            completedOrderId: () => undefined,
            positionIntents: ({ context, event }) => [...context.positionIntents, event.value]
          })
        },
        sendCart: {
          target: 'SendingOrder',
          actions: assign({
            sendCartParams: ({ event }) => event.sendCartParams,
            blockedPositionsDialogAcknowledgedForCartId: () => null
          })
        },
        cancelCart: {
          target: 'CancellingOrder',
          actions: assign({
            cancelReasonId: () => CANCEL_REASONS.CANCELLED_BY_CUSTOMER
          })
        },
        changeDeliveryDateOfCart: {
          target: 'ChangingDeliveryDateOfCart'
        }
      }
    },
    ChangingDeliveryDateOfCart: {
      initial: 'UserInputDeliveryDate',
      states: {
        UserInputDeliveryDate: {
          on: {
            chooseDeliveryDate: {
              target: 'ProcessingChangeDeliveryDateOfCart',
              actions: assign({
                desiredDeliveryDate: ({ event }) => event.desiredDeliveryDate
              })
            },
            abortUserInputForCart: {
              target: 'FinishedChangeDeliveryDateOfCart',
              actions: assign({
                desiredDeliveryDate: () => undefined
              })
            }
          }
        },
        ProcessingChangeDeliveryDateOfCart: {
          invoke: {
            src: 'setDeliveryDate',
            input: ({ context }) => ({
              desiredDeliveryDate: context.desiredDeliveryDate! as any, // das Frontend sendet einen String, welchen das Backend in ein Datum parst
              orderId: context.currentOrder!.id
            }),
            onDone: [
              ...defaultTransitions,
              {
                guard: guardHasAvailabilityOptions,
                target: 'HandlingPositionAvailabilityOptions',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order,
                  cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                  positionAvailabilityOptions: ({ event }) => event.output.positionAvailabilityOptions
                })
              },
              {
                target: 'FinishedChangeDeliveryDateOfCart',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order,
                  cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                  positionAvailabilityOptions: () => undefined
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        HandlingPositionAvailabilityOptions: {
          invoke: {
            id: POSITION_AVAILABILITY_OPTIONS_ACTOR_ID,
            src: POSITION_AVAILABILITY_OPTIONS_ACTOR,
            input: ({ context }) => context.positionAvailabilityOptions!,
            onDone: [
              ...defaultTransitions,
              {
                target: 'FinishedChangeDeliveryDateOfCart',
                actions: assign({
                  positionAvailabilityOptions: () => undefined,
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        FinishedChangeDeliveryDateOfCart: {
          type: 'final'
        }
      },
      onDone: {
        target: 'Idle'
      }
    },
    ProcessingPositions: {
      on: {
        addPositionIntent: {
          actions: assign({
            positionIntents: ({ context, event }) => [...context.positionIntents, event.value]
          })
        }
      },
      initial: 'CheckingCartStatus',
      states: {
        CheckingCartStatus: {
          invoke: {
            src: 'fetchCartStatus',
            input: ({ context }) => context.currentOrder?.id,
            onDone: [
              ...defaultTransitions,
              {
                guard: ({ event }) => !event.output.exists,
                target: 'CreatingNewOrder',
                actions: assign({
                  currentOrder: () => undefined
                })
              },
              {
                guard: ({ event, context }) => event.output.lastModified !== context.currentOrder?.lastModified,
                target: 'RefetchingOrder'
              },
              {
                target: 'SchedulePositionIntent'
              }
            ],
            onError: [...errorTransitions]
          }
        },
        RefetchingOrder: {
          invoke: {
            src: 'fetchOrder',
            input: ({ context }) => context.currentOrder?.id,
            onDone: [
              ...defaultTransitions,
              {
                guard: ({ event }) => event.output.order === undefined,
                target: 'CreatingNewOrder',
                actions: assign({
                  currentOrder: () => undefined
                })
              },
              {
                target: 'SchedulePositionIntent',
                actions: assign({
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        CreatingNewOrder: {
          initial: 'DeliveryDateSelection',
          states: {
            DeliveryDateSelection: {
              on: {
                chooseDeliveryDate: {
                  target: 'Creating',
                  actions: assign({
                    desiredDeliveryDate: ({ event }) => event.desiredDeliveryDate
                  })
                },
                abortUserInputForCart: {
                  target: '#Idle',
                  actions: assign({
                    desiredDeliveryDate: () => undefined,
                    positionIntents: () => []
                  })
                }
              }
            },
            Creating: {
              invoke: {
                src: 'createNewOrder',
                input: ({ context }) => context.desiredDeliveryDate!,
                onDone: [
                  {
                    guard: ({ event }) => !event.output.order,
                    target: '#CartError'
                  },
                  {
                    target: 'OrderCreated',
                    actions: assign({
                      cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                      currentOrder: ({ event }) => event.output.order
                    })
                  }
                ],
                onError: [...errorTransitions]
              }
            },
            OrderCreated: {
              type: 'final'
            }
          },
          onDone: {
            target: 'SchedulePositionIntent'
          }
        },
        SchedulePositionIntent: {
          after: {
            // Es wäre eleganter, eine `always`-transition zu verwenden.
            // Bei der `always`-transition wird aber der `useSelector`-Hook nicht mit dem state SchedulePositionIntent getriggert
            // und actor.subscribe() triggert den Observer auch nicht mit dem `SchedulePositionIntent` ... :-(
            10: [
              {
                guard: ({ context }) => context.positionIntents.length > 0,
                target: 'ProcessingPositionIntent',
                actions: assign({
                  scheduledPositionIntent: ({ context }) => context.positionIntents[0],
                  positionIntents: ({ context }) => context.positionIntents.slice(1)
                })
              },
              {
                target: 'AllPositionsProcessed',
                actions: assign({
                  scheduledPositionIntent: () => undefined,
                  positionIntents: () => []
                })
              }
            ]
          }
        },
        ProcessingPositionIntent: {
          invoke: {
            src: 'processPositionIntent',
            input: ({ context }) => {
              if (!context.scheduledPositionIntent) throw new Error('No scheduled position intent');
              return {
                orderId: context.currentOrder!.id,
                currentOrder: context.currentOrder,
                positionIntent: context.scheduledPositionIntent
              };
            },
            onDone: [
              ...defaultTransitions,
              {
                guard: guardHasAvailabilityOptions,
                target: 'HandlingPositionAvailabilityOptions',
                actions: assign({
                  cartMessages: ({ event }) => event.output.messages?.map(m => ({ messageText: m })) ?? [],
                  positionAvailabilityOptions: ({ event }) => event.output.positionAvailabilityOptions
                })
              },
              {
                guard: ({ event, context }) => {
                  const backendMessageCount = event?.output?.messages?.length ?? 0;
                  if (
                    backendMessageCount === 0 ||
                    apiResponseSignalsBlockedPositionsButShouldBeSuppressed(event.output, context)
                  ) {
                    return false;
                  }
                  return true;
                },
                target: 'PositionIntentError',
                actions: assign({
                  scheduledPositionIntent: undefined,
                  cartMessages: ({ event }) => {
                    if (!event.output.articleDescriptionForErrorMessage) {
                      return event.output.messages?.map(m => ({ messageText: m })) ?? [];
                    } else {
                      return [
                        { messageText: event.output.articleDescriptionForErrorMessage },
                        ...(event.output.messages?.map(m => ({ messageText: m })) ?? [])
                      ];
                    }
                  },
                  currentOrder: ({ event }) => event.output.order,
                  isCsvImport: ({ event }) => event.output.isCsvImport
                })
              },
              {
                target: 'SchedulePositionIntent',
                actions: assign({
                  scheduledPositionIntent: undefined,
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        HandlingPositionAvailabilityOptions: {
          invoke: {
            id: POSITION_AVAILABILITY_OPTIONS_ACTOR_ID,
            src: POSITION_AVAILABILITY_OPTIONS_ACTOR,
            input: ({ context }) => context.positionAvailabilityOptions!,
            onDone: [
              ...defaultTransitions,
              {
                target: 'SchedulePositionIntent',
                actions: assign({
                  positionAvailabilityOptions: () => undefined,
                  scheduledPositionIntent: undefined,
                  currentOrder: ({ event }) => event.output.order
                })
              }
            ],
            onError: [...errorTransitions]
          }
        },
        PositionIntentError: {
          on: {
            abortUserInputForCart: {
              target: 'SchedulePositionIntent',
              actions: assign({
                cartMessages: () => []
              })
            },
            acknowledgeBlockedPositionsDialog: {
              target: 'SchedulePositionIntent',
              actions: assign({
                cartMessages: () => [],
                blockedPositionsDialogAcknowledgedForCartId: ({ context }) => context.currentOrder?.id ?? null
              })
            }
          }
        },
        AllPositionsProcessed: {
          type: 'final'
        }
      },
      output: {
        positionIntents: ({ context }) => context.positionIntents
      },
      onDone: [
        {
          target: 'Idle'
        }
      ]
    }
  }
});
